import React, { useState, useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useNavigate } from 'react-router-dom';
import GameDashboard from './components/GameDashboard';
import StageContent from './components/StageContent';
import ChallengeInterface from './components/ChallengeInterface';
import VideosPage from './components/VideosPage';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';
import { Toaster } from './components/ui/toaster';
import { toast } from './hooks/use-toast';
import api from './utils/api';

// Wrapper component for Admin Login with navigation
const AdminLoginWrapper = () => {
  const navigate = useNavigate();
  
  const handleLoginSuccess = () => {
    navigate('/empress-admin-dashboard-2024');
  };
  
  return <AdminLogin onLoginSuccess={handleLoginSuccess} />;
};

// Wrapper component for Admin Dashboard with navigation
const AdminDashboardWrapper = () => {
  const navigate = useNavigate();
  
  // Check if user is authenticated
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/empress-admin-portal-2024');
    }
  }, [navigate]);
  
  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/empress-admin-portal-2024');
  };
  
  return <AdminDashboard onLogout={handleLogout} />;
};

function App() {
  const [stages, setStages] = useState([]);
  const [challenges, setChallenges] = useState({});
  const [userProgress, setUserProgress] = useState(null);
  const [currentView, setCurrentView] = useState('dashboard'); // dashboard, stage, challenge, videos
  const [selectedStage, setSelectedStage] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch initial data on mount
  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      
      // Fetch stages and user progress in parallel
      const [stagesData, progressData] = await Promise.all([
        api.getStages(),
        api.getUserProgress()
      ]);

      // Update stages based on progress
      const updatedStages = stagesData.map(stage => {
        if (progressData.completedStages.includes(stage.id)) {
          return { ...stage, completed: true, unlocked: true };
        }
        if (stage.id === 1 || progressData.completedStages.includes(stage.id - 1)) {
          return { ...stage, unlocked: true };
        }
        return stage;
      });

      setStages(updatedStages);
      setUserProgress(progressData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching initial data:', error);
      toast({
        title: "Error loading game data",
        description: "Please refresh the page to try again.",
        variant: "destructive"
      });
      setLoading(false);
    }
  };

  const handleStageSelect = async (stage) => {
    setSelectedStage(stage);
    
    // Fetch challenges for this stage if not already loaded
    if (!challenges[stage.id]) {
      try {
        const stageChallenges = await api.getChallenges(stage.id);
        setChallenges(prev => ({ ...prev, [stage.id]: stageChallenges }));
      } catch (error) {
        console.error(`Error loading challenges for stage ${stage.id}:`, error);
        toast({
          title: "Error loading challenges",
          description: "Please try again.",
          variant: "destructive"
        });
        return;
      }
    }
    
    setCurrentView('stage');
  };

  const handleStartChallenge = () => {
    setCurrentView('challenge');
  };

  const handleChallengeComplete = async (stageId, score, attempts) => {
    try {
      // Update progress on backend
      const response = await api.updateUserProgress(stageId, score, attempts);
      
      if (response.success) {
        const newProgress = response.newProgress;
        setUserProgress(newProgress);

        // Update stages
        const updatedStages = stages.map(stage => {
          if (stage.id === stageId) {
            return { ...stage, completed: true };
          }
          if (stage.id === stageId + 1) {
            return { ...stage, unlocked: true };
          }
          return stage;
        });
        setStages(updatedStages);

        toast({
          title: "Stage Complete! 🎉",
          description: `You've completed ${selectedStage.title}. Next stage unlocked!`,
        });
      }

      // Return to dashboard
      setCurrentView('dashboard');
      setSelectedStage(null);
    } catch (error) {
      console.error('Error updating progress:', error);
      toast({
        title: "Error saving progress",
        description: "Your progress may not be saved. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleBackToDashboard = () => {
    setCurrentView('dashboard');
    setSelectedStage(null);
  };

  const handleBackToStage = () => {
    setCurrentView('stage');
  };

  const handleStage6Complete = async () => {
    try {
      // Mark Stage 6 as completed (no challenge, just viewing is completion)
      const response = await api.updateUserProgress(6, 0, 0);
      
      if (response.success) {
        const newProgress = response.newProgress;
        setUserProgress(newProgress);

        // Update stages to mark Stage 6 as completed
        const updatedStages = stages.map(stage => {
          if (stage.id === 6) {
            return { ...stage, completed: true };
          }
          return stage;
        });
        setStages(updatedStages);
      }
    } catch (error) {
      console.error('Error completing Stage 6:', error);
      // Don't show error toast for this since it's automatic
    }
  };

  const handleNavigateToVideos = () => {
    setCurrentView('videos');
    setSelectedStage(null);
  };

  const handleBackFromVideos = () => {
    setCurrentView('dashboard');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-700 mb-4"></div>
          <p className="text-slate-600 text-lg">Loading Beneficiaries Blueprint...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route
            path="/"
            element={
              currentView === 'dashboard' ? (
                <GameDashboard
                  stages={stages}
                  userProgress={userProgress}
                  onStageSelect={handleStageSelect}
                  onNavigateToVideos={handleNavigateToVideos}
                />
              ) : currentView === 'stage' ? (
                <StageContent
                  stage={selectedStage}
                  onBack={handleBackToDashboard}
                  onStartChallenge={handleStartChallenge}
                  userProgress={userProgress}
                  stages={stages}
                  onStage6Complete={handleStage6Complete}
                />
              ) : currentView === 'videos' ? (
                <VideosPage
                  onBack={handleBackFromVideos}
                />
              ) : (
                <ChallengeInterface
                  stage={selectedStage}
                  challenges={challenges[selectedStage.id] || []}
                  onBack={handleBackToStage}
                  onComplete={handleChallengeComplete}
                  userProgress={userProgress}
                />
              )
            }
          />
          <Route path="/empress-admin-portal-2024" element={<AdminLoginWrapper />} />
          <Route path="/empress-admin-dashboard-2024" element={<AdminDashboardWrapper />} />
        </Routes>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
